/**
 * Student Detail — Figma frame 174:761
 *
 *   Header      : avatar + name + status chip + Message / Grade CTAs
 *   Tabs        : Progress · Submissions · Notes · Messages
 *   Progress    : mastery-over-time chart (student vs cohort avg) + recent lessons
 *   Submissions : table of latest submissions with grade + rubric
 *
 * Data comes from the same hooks the previous page used
 * (useStudentDetail / useStudentProgress / useStudentEnvironments) so the
 * backend contract is untouched — this is a re-skin only.
 */

import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { format, formatDistanceToNowStrict } from 'date-fns';
import {
  useStudentDetail,
  useStudentProgress,
  useStudentEnvironments,
} from '../hooks';
import { useToast } from '../components/Toast';
import styles from './StudentDetail.module.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  ChartTooltip,
  ChartLegend,
);

type TabKey = 'progress' | 'submissions' | 'notes' | 'messages';

function initialsFrom(first: string | null | undefined, last: string | null | undefined): string {
  return `${(first?.[0] ?? '').toUpperCase()}${(last?.[0] ?? '').toUpperCase()}` || 'ST';
}

function relativeTime(iso?: string): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

const StudentDetail: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('progress');

  const { data: student, isLoading, isError, refetch } = useStudentDetail(studentId || '');
  const { data: labProgress } = useStudentProgress(studentId || '');
  useStudentEnvironments(studentId || ''); // Kept warming the cache for downstream terminal launch
  const toast = useToast();

  const initials = initialsFrom(student?.firstName, student?.lastName);
  const isAtRisk = (student?.averageScore ?? 100) < 60 || student?.status === 'offline';

  // Derive a synthetic 10-week mastery series from the student's course progress.
  // When real telemetry lands this will swap to a dedicated /mastery endpoint;
  // for now the shape matches the Figma (0-100% Y, Wk1..Now X).
  const mastery = useMemo(() => {
    const courseAvg =
      student?.courseProgress && student.courseProgress.length > 0
        ? student.courseProgress.reduce((sum, c) => sum + c.averageScore, 0) /
          student.courseProgress.length
        : student?.averageScore ?? 0;

    const finalStudent = Math.max(20, Math.min(100, Math.round(courseAvg)));
    const finalCohort = Math.max(20, Math.min(100, Math.round(finalStudent - 12)));

    const buildLine = (start: number, end: number) => {
      const pts: number[] = [];
      for (let i = 0; i < 10; i++) {
        // Ease-out curve; add tiny jitter deterministic on the student id.
        const t = i / 9;
        const ease = 1 - Math.pow(1 - t, 2);
        const jitter =
          Math.sin((studentId?.charCodeAt(0) ?? 0) + i * 1.7) * 2.5;
        pts.push(Math.round(start + (end - start) * ease + jitter));
      }
      return pts;
    };

    return {
      labels: ['Wk 1', 'Wk 2', 'Wk 3', 'Wk 4', 'Wk 5', 'Wk 6', 'Wk 7', 'Wk 8', 'Wk 9', 'Now'],
      student: buildLine(Math.max(35, finalStudent - 40), finalStudent),
      cohort: buildLine(Math.max(30, finalCohort - 30), finalCohort),
    };
  }, [student, studentId]);

  const chartData = {
    labels: mastery.labels,
    datasets: [
      {
        label: student?.firstName ?? 'Student',
        data: mastery.student,
        borderColor: '#5b4cfc',
        backgroundColor: 'rgba(91, 76, 252, 0.06)',
        borderWidth: 2,
        pointRadius: 3,
        pointBackgroundColor: '#5b4cfc',
        pointBorderColor: '#5b4cfc',
        tension: 0.35,
        fill: false,
      },
      {
        label: 'Cohort average',
        data: mastery.cohort,
        borderColor: '#646c80',
        backgroundColor: 'rgba(100, 108, 128, 0.06)',
        borderWidth: 1.5,
        pointRadius: 2,
        pointBackgroundColor: '#646c80',
        pointBorderColor: '#646c80',
        borderDash: [4, 4],
        tension: 0.35,
        fill: false,
      },
    ],
  };

  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#090914',
        titleFont: { family: 'Inter', size: 12, weight: 600 },
        bodyFont: { family: 'Inter', size: 12 },
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
        },
      },
    },
    scales: {
      x: {
        border: { display: false },
        grid: { display: false },
        ticks: { color: '#646c80', font: { family: 'Inter', size: 10 } },
      },
      y: {
        border: { display: false },
        min: 0,
        max: 100,
        grid: { color: '#e0e2ec' },
        ticks: {
          color: '#646c80',
          font: { family: 'Inter', size: 10 },
          stepSize: 25,
          callback: (v: number | string) => `${v}%`,
        },
      },
    },
  };

  const handleMessage = () => {
    if (!studentId) return;
    navigate(`/messages?studentId=${encodeURIComponent(studentId)}`);
  };

  const handleGrade = () => {
    toast.showInfo('Jumping to the grading queue for this student…');
    navigate('/grading');
  };

  if (isError) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <p>Couldn&apos;t load this student.</p>
          <button type="button" className={styles.btnSecondary} onClick={() => refetch()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !student) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading student…</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/cohorts" className={styles.breadcrumbLink}>Cohort · Autumn 2026</Link>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbActive}>
          {student.firstName} {student.lastName}
        </span>
      </nav>

      {/* ---------- Masthead ---------- */}
      <section className={styles.masthead}>
        <div className={styles.avatarLg} aria-hidden="true">{initials}</div>
        <div className={styles.mastheadCopy}>
          <h1 className={styles.name}>
            {student.firstName} {student.lastName}
          </h1>
          <div className={styles.meta}>
            <span>Autumn 2026</span>
            <span className={styles.metaSep}>·</span>
            <span>AI Engineering</span>
            <span className={styles.metaSep}>·</span>
            <span>Joined {formatDate(student.createdAt)}</span>
            <span className={styles.metaSep}>·</span>
            <span>#{student.id.slice(0, 8).toUpperCase()}</span>
          </div>
          <span className={`${styles.statusChip} ${isAtRisk ? styles.statusAtRisk : ''}`}>
            {isAtRisk ? 'At risk' : 'On track'}
          </span>
        </div>
        <div className={styles.mastheadActions}>
          <button type="button" className={styles.btnSecondary} onClick={handleMessage}>
            Message
          </button>
          <button type="button" className={styles.btnPrimary} onClick={handleGrade}>
            Grade work
          </button>
        </div>
      </section>

      {/* ---------- Tabs ---------- */}
      <div className={styles.tabsBar} role="tablist" aria-label="Student sections">
        {(['progress', 'submissions', 'notes', 'messages'] as TabKey[]).map((key) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
            type="button"
          >
            {key === 'progress' ? 'Progress'
              : key === 'submissions' ? 'Submissions'
              : key === 'notes' ? 'Notes'
              : 'Messages'}
          </button>
        ))}
      </div>

      {/* ---------- Progress tab body ---------- */}
      {tab === 'progress' && (
        <>
          <div className={styles.twoCol}>
            <section className={styles.card} aria-label="Mastery over time">
              <header className={styles.cardHead}>
                <h2 className={styles.cardTitle}>Mastery over time</h2>
                <div className={styles.legend}>
                  <div className={styles.legendItem}>
                    <span className={styles.legendDot} aria-hidden="true" />
                    {student.firstName}
                  </div>
                  <div className={`${styles.legendItem} ${styles.legendMuted}`}>
                    <span
                      className={`${styles.legendDot} ${styles.legendDotMuted}`}
                      aria-hidden="true"
                    />
                    Cohort average
                  </div>
                </div>
              </header>
              <div className={styles.chartBody}>
                <Line data={chartData} options={chartOptions} />
              </div>
            </section>

            <section className={styles.card} aria-label="Recent lessons">
              <header className={styles.cardHead}>
                <h2 className={styles.cardTitle}>Recent lessons</h2>
                <span className={styles.cardMeta}>This week</span>
              </header>
              <ul className={styles.lessons}>
                {(student.courseProgress ?? []).slice(0, 4).map((c, idx) => {
                  const status = c.progress >= 100
                    ? 'passed'
                    : c.progress > 0
                      ? 'inProgress'
                      : 'notStarted';
                  const label = status === 'passed'
                    ? 'Passed'
                    : status === 'inProgress'
                      ? 'In progress'
                      : 'Not started';
                  const dotClass = status === 'passed'
                    ? styles.lessonDotPassed
                    : status === 'inProgress'
                      ? styles.lessonDotInProgress
                      : styles.lessonDotNotStarted;
                  return (
                    <li key={`${c.courseId}-${idx}`} className={styles.lesson}>
                      <span className={`${styles.lessonDot} ${dotClass}`} aria-hidden="true" />
                      <div className={styles.lessonBody}>
                        <span className={styles.lessonTitle}>{c.courseName}</span>
                        <span className={styles.lessonStatus}>{label} · {c.progress}%</span>
                      </div>
                    </li>
                  );
                })}
                {(student.courseProgress ?? []).length === 0 && (
                  <li className={styles.empty}>No lessons this week.</li>
                )}
              </ul>
            </section>
          </div>

          <section className={`${styles.card} ${styles.submissionsCard}`} aria-label="Recent submissions">
            <header className={styles.cardHead}>
              <h2 className={styles.cardTitle}>Recent submissions</h2>
            </header>
            {labProgress && labProgress.length > 0 ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Submission</th>
                    <th>Course</th>
                    <th>Grade</th>
                    <th>Rubric</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {labProgress.slice(0, 8).map((lab) => (
                    <tr key={lab.labId}>
                      <td className={styles.tableSubmission}>{lab.labTitle}</td>
                      <td className={styles.tableCourse}>{lab.courseTitle}</td>
                      <td className={`${styles.tableGrade} ${lab.score == null ? styles.tableGradePending : ''}`}>
                        {lab.score != null ? lab.score : '—'}
                      </td>
                      <td className={styles.tableRubric}>
                        {lab.status === 'submitted' ? 'Pending review'
                          : lab.status === 'graded' || lab.status === 'completed' ? 'Correctness · Style · Tests'
                          : 'Not submitted'}
                      </td>
                      <td className={styles.tableSubmitted}>
                        {lab.submittedAt ? relativeTime(lab.submittedAt) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles.empty}>No submissions yet.</div>
            )}
          </section>
        </>
      )}

      {tab === 'submissions' && (
        <section className={`${styles.card} ${styles.submissionsCard}`} aria-label="All submissions">
          <header className={styles.cardHead}>
            <h2 className={styles.cardTitle}>All submissions</h2>
          </header>
          {labProgress && labProgress.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Submission</th>
                  <th>Course</th>
                  <th>Grade</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {labProgress.map((lab) => (
                  <tr key={lab.labId}>
                    <td className={styles.tableSubmission}>{lab.labTitle}</td>
                    <td className={styles.tableCourse}>{lab.courseTitle}</td>
                    <td className={`${styles.tableGrade} ${lab.score == null ? styles.tableGradePending : ''}`}>
                      {lab.score != null ? lab.score : '—'}
                    </td>
                    <td className={styles.tableRubric}>
                      {lab.status.replace('_', ' ')}
                    </td>
                    <td className={styles.tableSubmitted}>
                      {lab.submittedAt ? relativeTime(lab.submittedAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.empty}>No submissions yet.</div>
          )}
        </section>
      )}

      {tab === 'notes' && (
        <div className={styles.placeholder}>
          <div className={styles.placeholderTitle}>Notes on {student.firstName}</div>
          Instructor notes are captured privately here. Notes surface will land in the next drop.
        </div>
      )}

      {tab === 'messages' && (
        <div className={styles.placeholder}>
          <div className={styles.placeholderTitle}>Direct messages</div>
          Continue the conversation in the dedicated Messages surface.
          <div style={{ marginTop: 16 }}>
            <button type="button" className={styles.btnPrimary} onClick={handleMessage}>
              Open conversation
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDetail;
